import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  Slider,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/TuneRounded';
import { ReconnectingWebSocket } from '../../socket';

type BotType = 'dipster' | 'dumper';

interface OrderSizeConfigModalProps {
  open: boolean;
  onClose: () => void;
  botType: BotType;
  socket: ReconnectingWebSocket;
  onSuccess?: (message: string) => void;
}

const OrderSizeConfigModal: React.FC<OrderSizeConfigModalProps> = ({
  open,
  onClose,
  botType,
  socket,
  onSuccess,
}) => {
  const theme = useTheme();
  const [buyMinUsdt, setBuyMinUsdt] = useState<number>(5);
  const [buyMaxUsdt, setBuyMaxUsdt] = useState<number>(50);
  const [cycleMs, setCycleMs] = useState<number>(30000);
  const [sellMinRif, setSellMinRif] = useState<number>(250);
  const [sellMaxRif, setSellMaxRif] = useState<number>(500);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const accentColor = botType === 'dipster' ? theme.palette.success.main : theme.palette.warning.main;

  // Load current config when modal opens
  useEffect(() => {
    if (open && socket) {
      setInitialLoading(true);
      setError(null);
      console.log(`[OrderSizeConfigModal] Loading config for ${botType}`);

      const handleResponse = (payload: any) => {
        console.log(`[OrderSizeConfigModal] Received response:`, payload);
        setInitialLoading(false);
        if (payload.success && payload.config) {
          setBuyMinUsdt(payload.config.buyMinUsdt || 5);
          setBuyMaxUsdt(payload.config.buyMaxUsdt || 50);
          setCycleMs(payload.config.cycleMs || 30000);
          setSellMinRif(payload.config.sellMinRif || 250);
          setSellMaxRif(payload.config.sellMaxRif || 500);
        } else if (payload.error) {
          setError(payload.error);
        }
      };

      socket.on('getOrderSizeConfigResponse', handleResponse);
      socket.send('getOrderSizeConfig', { botType });

      return () => {
        socket.off('getOrderSizeConfigResponse', handleResponse);
      };
    }
  }, [open, botType, socket]);

  const handleSave = useCallback(() => {
    if (!socket) return;

    if (botType === 'dipster') {
      if (buyMinUsdt <= 0) { setError('Minimum buy size must be greater than 0'); return; }
      if (buyMaxUsdt < buyMinUsdt) { setError('Maximum buy size cannot be less than minimum'); return; }
    }
    if (cycleMs < 5000 || cycleMs > 3600000) { setError('Cycle interval must be between 5s and 1h'); return; }
    if (botType === 'dumper') {
      if (sellMinRif <= 0) { setError('Sell min slot must be greater than 0'); return; }
      if (sellMaxRif < sellMinRif) { setError('Sell max slot cannot be less than sell min slot'); return; }
    }

    setLoading(true);
    setError(null);

    const handleResponse = (payload: any) => {
      setLoading(false);
      socket.off('setOrderSizeConfigResponse', handleResponse);
      if (payload.success) {
        onSuccess?.(`Config updated for ${botType}`);
        onClose();
      } else {
        setError(payload.error || 'Failed to update configuration');
      }
    };

    socket.on('setOrderSizeConfigResponse', handleResponse);
    socket.send('setOrderSizeConfig', { botType, buyMinUsdt, buyMaxUsdt, cycleMs, sellMinRif, sellMaxRif });
  }, [socket, botType, buyMinUsdt, buyMaxUsdt, cycleMs, sellMinRif, sellMaxRif, onSuccess, onClose]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: `1px solid ${alpha(accentColor, 0.2)}`,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: alpha(accentColor, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TuneIcon sx={{ fontSize: 18, color: accentColor }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>Bot Configuration</Typography>
            <Typography variant="caption" color="text.secondary">
              {botType === 'dipster' ? 'Dipster — buy size & cycle' : 'Dumper — sell slot & cycle'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ py: 0 }}>{error}</Alert>}

            {/* ── BUY SIZE — dipster only ── */}
            {botType === 'dipster' && (
            <Box>
              <Typography variant="overline" color="text.secondary" fontSize="0.65rem" fontWeight={700}>
                Buy Order Size (adaptive)
              </Typography>
              <Stack direction="row" spacing={2} mt={0.5}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="caption" color="text.secondary">Mín (presión alta)</Typography>
                    <TextField value={buyMinUsdt} onChange={(e) => setBuyMinUsdt(Math.max(1, Number(e.target.value) || 1))}
                      type="number" size="small"
                      InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.3 }}>$</Typography> }}
                      sx={{ width: 130 }} inputProps={{ inputMode: 'decimal', style: { padding: '6px 10px' } }} />
                  </Stack>
                  <Slider value={buyMinUsdt} onChange={(_, v) => { const val = v as number; setBuyMinUsdt(val); if (buyMaxUsdt < val) setBuyMaxUsdt(val); }}
                    min={1} max={10000} step={1} size="small" sx={{ color: accentColor }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="caption" color="text.secondary">Máx (presión baja)</Typography>
                    <TextField value={buyMaxUsdt} onChange={(e) => setBuyMaxUsdt(Math.max(buyMinUsdt, Number(e.target.value) || buyMinUsdt))}
                      type="number" size="small"
                      InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.3 }}>$</Typography> }}
                      sx={{ width: 130 }} inputProps={{ inputMode: 'decimal', style: { padding: '6px 10px' } }} />
                  </Stack>
                  <Slider value={Math.min(buyMaxUsdt, 10000)} onChange={(_, v) => setBuyMaxUsdt(v as number)}
                    min={1} max={10000} step={1} size="small" sx={{ color: accentColor }} />
                </Box>
              </Stack>
            </Box>
            )}

            {/* ── SELL SLOT SIZE — dumper only ── */}
            {botType === 'dumper' && (
            <Box>
              <Typography variant="overline" color="text.secondary" fontSize="0.65rem" fontWeight={700}>
                Sell Slot Size (tokens/orden)
              </Typography>
              <Stack direction="row" spacing={2} mt={0.5}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="caption" color="text.secondary">Mínimo</Typography>
                    <TextField value={sellMinRif} onChange={(e) => setSellMinRif(Math.max(1, Number(e.target.value) || 1))}
                      type="number" size="small"
                      InputProps={{ endAdornment: <Typography variant="caption" sx={{ ml: 0.3 }}>RIF</Typography> }}
                      sx={{ width: 130 }} inputProps={{ inputMode: 'decimal', style: { padding: '6px 10px' } }} />
                  </Stack>
                  <Slider value={sellMinRif} onChange={(_, v) => { const val = v as number; setSellMinRif(val); if (sellMaxRif < val) setSellMaxRif(val); }}
                    min={250} max={500000} step={250} size="small" sx={{ color: accentColor }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="caption" color="text.secondary">Máximo</Typography>
                    <TextField value={sellMaxRif} onChange={(e) => setSellMaxRif(Math.max(sellMinRif, Number(e.target.value) || sellMinRif))}
                      type="number" size="small"
                      InputProps={{ endAdornment: <Typography variant="caption" sx={{ ml: 0.3 }}>RIF</Typography> }}
                      sx={{ width: 130 }} inputProps={{ inputMode: 'decimal', style: { padding: '6px 10px' } }} />
                  </Stack>
                  <Slider value={Math.min(sellMaxRif, 500000)} onChange={(_, v) => setSellMaxRif(v as number)}
                    min={250} max={500000} step={250} size="small" sx={{ color: accentColor }} />
                </Box>
              </Stack>
            </Box>
            )}

            {/* ── CYCLE INTERVAL ── */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="overline" color="text.secondary" fontSize="0.65rem" fontWeight={700}>
                  Intervalo entre órdenes
                </Typography>
                <TextField value={Math.round(cycleMs / 1000)}
                  onChange={(e) => setCycleMs(Math.min(300000, Math.max(5000, (Number(e.target.value) || 30) * 1000)))}
                  type="number" size="small"
                  InputProps={{ endAdornment: <Typography variant="caption" sx={{ ml: 0.3 }}>s</Typography> }}
                  sx={{ width: 110 }} inputProps={{ inputMode: 'decimal', style: { padding: '6px 10px' } }} />
              </Stack>
              <Slider value={Math.round(cycleMs / 1000)} onChange={(_, v) => setCycleMs((v as number) * 1000)}
                min={5} max={3600} step={5} size="small"
                marks={[
                  { value: 30, label: '30s' },
                  { value: 300, label: '5m' },
                  { value: 900, label: '15m' },
                  { value: 1800, label: '30m' },
                  { value: 3600, label: '1h' },
                ]}
                sx={{ color: accentColor, mb: 2 }} />
            </Box>

            {/* Summary strip */}
            <Box sx={{ display: 'flex', gap: 1, p: 1.5, bgcolor: alpha(accentColor, 0.05), borderRadius: 1.5, border: `1px solid ${alpha(accentColor, 0.1)}` }}>
              {([ 
                botType === 'dipster' ? { label: 'Buy/ciclo', value: `$${buyMinUsdt}–${buyMaxUsdt}` } : null,
                botType === 'dumper' ? { label: 'Sell RIF/ciclo', value: `${sellMinRif}–${sellMaxRif}` } : null,
                { label: 'Intervalo', value: `${Math.round(cycleMs / 1000)}s` },
              ] as Array<{ label: string; value: string } | null>).filter((x): x is { label: string; value: string } => x !== null).map(({ label, value }) => (
                <Box key={label} sx={{ flex: 1, textAlign: 'center' }}>
                  <Typography variant="subtitle2" fontWeight={700} color={accentColor}>{value}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1 }}>
        <Button onClick={onClose} disabled={loading} size="small">Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || initialLoading} size="small"
          sx={{ bgcolor: accentColor, '&:hover': { bgcolor: alpha(accentColor, 0.85) } }}>
          {loading ? <CircularProgress size={16} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderSizeConfigModal;
